/**
 * Maintenance Page
 * Displayed when the platform is undergoing maintenance
 */

import React, { useState, useEffect } from 'react';
import './ErrorPages.css';

const Maintenance = ({
  estimatedTime = null,
  message = null,
  showCountdown = true,
  // ServerOutageGuard reuses this screen for unplanned outages.
  title = 'Under Maintenance'
}) => {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (!estimatedTime || !showCountdown) return;

    const targetTime = new Date(estimatedTime).getTime();

    const updateCountdown = () => {
      const now = Date.now();
      const diff = targetTime - now;

      if (diff <= 0) {
        setTimeLeft(null);
        // Check if maintenance is over
        window.location.reload();
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeLeft({ hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [estimatedTime, showCountdown]);

  return (
    <div className="error-page maintenance">
      <div className="error-page-content">
        <div className="error-illustration" aria-hidden="true">
          <svg width="200" height="200" viewBox="0 0 200 200" fill="none">
            {/* Gear/maintenance illustration */}
            <circle cx="100" cy="100" r="80" fill="var(--maintenance-bg)" stroke="var(--maintenance-primary)" strokeWidth="2" strokeDasharray="8 4"/>

            {/* Main gear */}
            <g className="gear-spin">
              <circle cx="100" cy="100" r="30" fill="white" stroke="var(--maintenance-primary)" strokeWidth="3"/>
              <circle cx="100" cy="100" r="10" fill="var(--maintenance-primary)"/>
              {/* Gear teeth */}
              {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
                <rect
                  key={i}
                  x="95"
                  y="65"
                  width="10"
                  height="15"
                  rx="2"
                  fill="var(--maintenance-primary)"
                  transform={`rotate(${angle} 100 100)`}
                />
              ))}
            </g>

            {/* Wrench */}
            <g transform="translate(130, 55) rotate(45)">
              <rect x="0" y="0" width="8" height="40" rx="2" fill="var(--maintenance-secondary)"/>
              <circle cx="4" cy="5" r="8" fill="none" stroke="var(--maintenance-secondary)" strokeWidth="3"/>
            </g>

            {/* Progress bar */}
            <rect x="50" y="155" width="100" height="8" rx="4" fill="var(--maintenance-secondary)" opacity="0.3"/>
            <rect x="50" y="155" width="60" height="8" rx="4" fill="var(--maintenance-primary)" className="progress-animate"/>
          </svg>
        </div>

        <h1 className="error-title">{title}</h1>

        <p className="error-message">
          {message || "We're performing scheduled maintenance to improve your experience. We'll be back shortly!"}
        </p>

        {timeLeft && (
          <div className="countdown-container">
            <p className="countdown-label">Estimated time remaining:</p>
            <div className="countdown-timer" role="timer" aria-live="polite">
              <div className="countdown-unit">
                <span className="countdown-value">{String(timeLeft.hours).padStart(2, '0')}</span>
                <span className="countdown-label">Hours</span>
              </div>
              <span className="countdown-separator">:</span>
              <div className="countdown-unit">
                <span className="countdown-value">{String(timeLeft.minutes).padStart(2, '0')}</span>
                <span className="countdown-label">Minutes</span>
              </div>
              <span className="countdown-separator">:</span>
              <div className="countdown-unit">
                <span className="countdown-value">{String(timeLeft.seconds).padStart(2, '0')}</span>
                <span className="countdown-label">Seconds</span>
              </div>
            </div>
          </div>
        )}

        <div className="maintenance-updates">
          <h3>Stay Updated</h3>
          <div className="update-links">
            <a href="/status" target="_blank" rel="noopener noreferrer" className="update-link">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
              </svg>
              Status Page
            </a>
          </div>
        </div>

        <div className="error-help">
          <p>Questions? Contact us at <a href="mailto:support@canira.io">support@canira.io</a></p>
        </div>
      </div>
    </div>
  );
};

export default Maintenance;
